/**
 * Homepage behaviour: the garage (dm-home-garage), owner video posters
 * (dm-home-proof) and click tracking for every [data-dmh-event] element.
 *
 * Garage (dm-home-garage): [data-dmh-garage] holds one [data-dmh-garage-fam] per car
 * family, each a [data-dmh-garage-tile] button and its panel of cars. One family
 * is open; its panel takes the grid row right after its tile's row. When all
 * tiles fit one row (wide screens), hovering a tile opens it after a short
 * pause, so a car is one click away. The family of the visitor's last car
 * (localStorage "dm:cars", written by assets/dm-header.js) opens first.
 * The search field filters [data-dmh-garage-car] links by car and family title
 * (case, spaces and dots ignored); Enter opens the first match, arrow keys move
 * through the list. Without this script every family shows open, no search.
 *
 * Video posters: pressing [data-dmh-yt-play] inside [data-dmh-yt="<id>"] swaps
 * the poster for a youtube-nocookie player that starts playing, and sends
 * proof_video_play. Nothing from YouTube loads before that click.
 *
 * Tracking: a click on any [data-dmh-event] element sends that event through
 * window.dmTrack, with every data-dmh-p-* attribute as a parameter
 * (data-dmh-p-model="992.2 GT3" becomes { model: "992.2 GT3" }).
 */
(function () {
  function track(name, params) {
    if (typeof window.dmTrack === 'function') window.dmTrack(name, params);
  }

  function initGarage(root) {
    if (root.dataset.dmhGarageReady) return;
    root.dataset.dmhGarageReady = 'true';
    const board = root.querySelector('[data-dmh-garage-board]');
    const families = Array.from(root.querySelectorAll('[data-dmh-garage-fam]'));
    if (!board || !families.length) return;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const tileOf = (family) => family.querySelector('[data-dmh-garage-tile]');
    const panelOf = (family) => family.querySelector('.dmh-garage__panel');
    const isOpen = (family) => family.classList.contains('is-open');
    const columns = () => getComputedStyle(board).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const oneRow = () => columns() >= families.length;

    // The open family's cars take the grid row right after its tile's row.
    function place() {
      families.forEach((family) => {
        const panel = panelOf(family);
        panel.style.gridRow = isOpen(family) ? String(Math.floor(families.indexOf(family) / columns()) + 2) : '';
      });
    }

    function show(target, keepInView) {
      const tile = target ? tileOf(target) : null;
      const before = tile ? tile.getBoundingClientRect().top : 0;
      families.forEach((family) => {
        const open = family === target;
        family.classList.toggle('is-open', open);
        tileOf(family).setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      place();
      if (keepInView && tile) {
        const shift = tile.getBoundingClientRect().top - before;
        if (Math.abs(shift) > 1) window.scrollBy(0, shift);
      }
    }

    let start = null;
    try {
      const last = (JSON.parse(localStorage.getItem('dm:cars') || '[]') || [])[0];
      if (last && last.u) {
        start = families.find((family) =>
          Array.from(family.querySelectorAll('a[href]')).some((a) => a.getAttribute('href') === last.u)
        ) || null;
      }
    } catch (error) {
      start = null;
    }
    root.classList.add('is-ready');
    show(start || families[0]);

    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(place, 120);
    });

    families.forEach((family) => {
      const tile = tileOf(family);
      let hoverTimer = 0;
      tile.addEventListener('click', () => {
        if (!isOpen(family)) {
          show(family, true);
          const name = family.querySelector('.dmh-garage__name');
          track('car_family_open', { family: name ? name.textContent.trim() : '', source: 'garage' });
        } else if (!oneRow()) {
          show(null, true);
        }
      });
      tile.addEventListener('pointerenter', () => {
        if (!finePointer.matches || !oneRow()) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          if (!isOpen(family)) show(family);
        }, 140);
      });
      tile.addEventListener('pointerleave', () => clearTimeout(hoverTimer));
    });

    initGarageSearch(root);
  }

  function initGarageSearch(root) {
    const box = root.querySelector('[data-dmh-garage-search]');
    const input = root.querySelector('[data-dmh-garage-input]');
    const list = root.querySelector('[data-dmh-garage-results]');
    const status = root.querySelector('[data-dmh-garage-status]');
    const emptyTemplate = root.querySelector('[data-dmh-garage-empty]');
    if (!box || !input || !list) return;
    const norm = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    // A token matches the car title with spaces and dots removed ("gt3rs" finds 991.2 GT3 RS),
    // or one whole word of the family title ("s" finds the Carrera, S, 4S & GTS family), never
    // letters run together across family words ("rs" must not find "S/T, R & Speedster").
    const cars = Array.from(root.querySelectorAll('[data-dmh-garage-car]')).map((a, index) => ({
      index,
      href: a.getAttribute('href'),
      title: a.dataset.dmhGarageTitle || '',
      family: a.dataset.dmhGarageFamily || '',
      key: norm(a.dataset.dmhGarageTitle),
      words: '|' + String(a.dataset.dmhGarageFamily || '').split(/\s+/).map(norm).filter(Boolean).join('|') + '|',
    }));
    const matches = (car, token) => car.key.indexOf(token) !== -1 || car.words.indexOf(token) !== -1;
    const emptyText = emptyTemplate ? emptyTemplate.innerHTML.trim() : '';
    box.hidden = false;

    function close() {
      list.hidden = true;
      list.textContent = '';
    }

    function render() {
      const tokens = input.value.split(/\s+/).map(norm).filter(Boolean);
      list.textContent = '';
      if (!tokens.length) {
        close();
        if (status) status.textContent = '';
        return;
      }
      const hits = cars
        .filter((car) => tokens.every((token) => matches(car, token)))
        .map((car) => ({
          car,
          score: (tokens.every((token) => car.key.indexOf(token) !== -1) ? 0 : 2) + (car.key.indexOf(tokens[0]) === 0 ? 0 : 1),
        }))
        .sort((a, b) => a.score - b.score || a.car.index - b.car.index)
        .slice(0, 8)
        .map((hit) => hit.car);
      if (!hits.length) {
        const li = document.createElement('li');
        li.className = 'dmh-garage__result-empty';
        li.textContent = emptyText;
        list.appendChild(li);
      }
      hits.forEach((car) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'dmh-garage__result';
        a.href = car.href;
        a.dataset.dmhEvent = 'chassis_pick';
        a.dataset.dmhPModel = car.title;
        a.dataset.dmhPSource = 'garage_search';
        const title = document.createElement('span');
        title.className = 'dmh-garage__result-title';
        title.textContent = car.title;
        const family = document.createElement('span');
        family.className = 'dmh-garage__result-family';
        family.textContent = car.family;
        a.append(title, family);
        li.appendChild(a);
        list.appendChild(li);
      });
      list.hidden = false;
      if (status) status.textContent = hits.length ? hits.length + (hits.length === 1 ? ' car' : ' cars') : emptyText;
    }

    const links = () => Array.from(list.querySelectorAll('a'));
    input.addEventListener('input', render);
    input.addEventListener('focus', () => {
      if (input.value) render();
    });
    input.addEventListener('keydown', (event) => {
      const first = links()[0];
      if (event.key === 'Enter' && first) {
        event.preventDefault();
        first.click();
      } else if (event.key === 'ArrowDown' && first) {
        event.preventDefault();
        first.focus();
      } else if (event.key === 'Escape') {
        input.value = '';
        render();
      }
    });
    list.addEventListener('keydown', (event) => {
      const all = links();
      const at = all.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' && at < all.length - 1) {
        event.preventDefault();
        all[at + 1].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        (at > 0 ? all[at - 1] : input).focus();
      } else if (event.key === 'Escape') {
        input.focus();
        close();
      }
    });
    document.addEventListener('click', (event) => {
      if (!box.contains(event.target)) close();
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-dmh-garage]').forEach(initGarage);
  }

  function playVideo(frame) {
    const id = frame.getAttribute('data-dmh-yt');
    if (!id || frame.querySelector('iframe')) return;
    const iframe = document.createElement('iframe');
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
    iframe.title = frame.getAttribute('data-dmh-yt-title') || 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    frame.textContent = '';
    frame.appendChild(iframe);
    frame.classList.add('is-playing');
    track('proof_video_play', { title: iframe.title });
  }

  if (!window.dmhClickTracking) {
    window.dmhClickTracking = true;
    document.addEventListener('click', (event) => {
      const play = event.target.closest('[data-dmh-yt-play]');
      if (play) {
        const frame = play.closest('[data-dmh-yt]');
        if (frame) playVideo(frame);
      }
      const el = event.target.closest('[data-dmh-event]');
      if (!el) return;
      // The last-car link is filled in by dm-header.js; name its car in the event.
      if (el.hasAttribute('data-dmh-picker-mine')) {
        const label = el.querySelector('[data-dm-my-car-label]');
        if (label) el.dataset.dmhPModel = label.textContent.trim();
      }
      const params = {};
      Object.keys(el.dataset).forEach((key) => {
        if (key.indexOf('dmhP') !== 0 || key.length < 5) return;
        const name = key.charAt(4).toLowerCase() + key.slice(5);
        params[name.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase())] = el.dataset[key];
      });
      track(el.dataset.dmhEvent, params);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
